import React from 'react';
import { X } from 'lucide-react';
import { LOSS_REASONS } from '../../../types';
import { AppSelectDropdown } from '../../../components/AppSelectDropdown';
import { fetchMe } from '../../../lib/meClient';
import {
  APP_BTN_DANGER_CLASS,
  APP_BTN_OUTLINE_CLASS,
  APP_INPUT_CLASS,
  APP_SELECT_TRIGGER_CLASS,
  APP_TEXTAREA_CLASS,
} from '../../../ui/controlStyles';
import { UI_Z_INDEX } from '../../../utils/uiZIndex';
import { useBackdropClose } from '../../../hooks/useDiscardGuard';

export type LeadLostPayload = {
  loss_reason: string;
  loss_comment?: string;
  lost_at: string;
};

export interface LeadLostModalProps {
  open: boolean;
  onClose: () => void;
  /** Return a rejected promise or throw on failure; parent may set `error` from API body. */
  onConfirm: (payload: LeadLostPayload) => Promise<void>;
  isSaving?: boolean;
  error?: string | null;
}

const LABEL_CLASS = 'mb-1 block text-xs font-medium text-app-text-muted';

function actorDisplayName(user: { username?: string; email?: string } | undefined): string {
  return String(user?.username ?? '').trim() || String(user?.email ?? '').trim();
}

export function LeadLostModal({ open, onClose, onConfirm, isSaving, error }: LeadLostModalProps) {
  const [lossReason, setLossReason] = React.useState('');
  const [lossComment, setLossComment] = React.useState('');
  const [markedBy, setMarkedBy] = React.useState('');

  React.useEffect(() => {
    if (!open) return;
    setLossReason('');
    setLossComment('');
    setMarkedBy('');
    let cancelled = false;
    fetchMe()
      .then((me) => {
        if (cancelled) return;
        setMarkedBy(actorDisplayName(me?.user));
      })
      .catch(() => {
        if (!cancelled) setMarkedBy('');
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Eine nach außen gezogene Textmarkierung schließt nicht.
  const backdropProps = useBackdropClose(onClose);

  if (!open) return null;

  const canSave = Boolean(lossReason.trim() && markedBy.trim()) && !isSaving;

  const handleConfirm = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!canSave) return;
    await onConfirm({
      loss_reason: lossReason.trim(),
      loss_comment: lossComment.trim() || undefined,
      lost_at: new Date().toISOString(),
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      {...backdropProps}
    >
      <div
        role="dialog"
        aria-modal="true"
        className="bg-app-surface rounded-xl shadow-xl border border-app-border p-6 w-full max-w-md mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <h3 className="text-lg font-semibold text-app-text">Projekt als verloren markieren</h3>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-app-text-subtle hover:bg-app-hover hover:text-app-text"
            aria-label="Schließen"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {error ? (
          <p className="mb-4 p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200">{error}</p>
        ) : null}
        <div className="space-y-4">
          <div>
            <label className={LABEL_CLASS} htmlFor="lead-lost-reason">
              Verlustgrund <span className="text-red-500">*</span>
            </label>
            <AppSelectDropdown
              id="lead-lost-reason"
              value={lossReason}
              onChange={setLossReason}
              placeholder="Bitte wählen…"
              searchable
              searchPlaceholder="Verlustgrund suchen…"
              triggerClassName={APP_SELECT_TRIGGER_CLASS}
              menuZIndex={UI_Z_INDEX.portalDropdownInModal}
              options={LOSS_REASONS.map((r) => ({ value: r, label: r }))}
            />
          </div>
          <div>
            <label className={LABEL_CLASS} htmlFor="lead-lost-comment">
              Kommentar
            </label>
            <textarea
              id="lead-lost-comment"
              value={lossComment}
              onChange={(e) => setLossComment(e.target.value)}
              rows={3}
              className={`${APP_TEXTAREA_CLASS} resize-none`}
              placeholder="Weitere Anmerkungen…"
            />
          </div>
          <div>
            <label className={LABEL_CLASS} htmlFor="lead-lost-marked-by">
              Markiert von
            </label>
            <input
              id="lead-lost-marked-by"
              type="text"
              value={markedBy}
              readOnly
              className={`${APP_INPUT_CLASS} cursor-default bg-app-surface-muted text-app-text-muted`}
              placeholder="Wird geladen…"
            />
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className={APP_BTN_OUTLINE_CLASS}>
            Abbrechen
          </button>
          <button
            type="button"
            onClick={(e) => void handleConfirm(e)}
            disabled={!canSave}
            className={APP_BTN_DANGER_CLASS}
          >
            {isSaving ? 'Speichert…' : 'Als verloren speichern'}
          </button>
        </div>
      </div>
    </div>
  );
}
